import React, {
  lazy,
  ReactNode,
  Suspense,
  useRef,
  useState,
  useTransition,
} from 'react';
import { includes } from 'lodash-es';

import { UnverifiedCompanyType } from '../../../../../common/constants';
import { useUnverifiedCompanyRestrictionModal } from '../../../../../common/hooks/useUnverifiedCompanyRestrictionModal/useUnverifiedCompanyRestrictionModal';
import {
  EmployerPurchaseProductType,
  SubscriptionPlanType,
} from '../../../../../common/interfaces/entities';
import { PurchaseSupportedPremiumPlanType } from '../../../../../common/interfaces/premium-plan';
import { trackPremiumPlanPurchaseCTAClicked } from '../../../../../common/tracking';
import ErrorBoundaryWithCrashReporting from '../../../../../components/Error/ErrorBoundaryWithCrashReporting';
import useShowCreditProductPurchaseSuccessModalAsAlert from '../../../hooks/useShowCreditProductPurchaseSuccessModalAsAlert';
import useShowCreditProductPurchaseSuccessModal from '../../CreditProductPurchaseSuccessModal/useShowCreditProductPurchaseSuccessModal';
import { CreditProductPurchaseOrderForAfterPayment } from '../../types';
import PremiumPlanPurchaseModalForAfterPayment from '../PremiumPlanPurchaseModalForAfterPayment';
import {
  OpenModalOptions,
  PremiumPlanPurchaseModalContext,
} from './PremiumPlanPurchaseModalContext';

const PremiumPlanPurchaseModal = lazy(
  () => import('../PremiumPlanPurchaseModal')
);

type ProviderProps = {
  children: ReactNode;
};

const AfterPaymentStateHandlers = () => {
  useShowCreditProductPurchaseSuccessModalAsAlert({
    productTypesToHandle: [EmployerPurchaseProductType.PREMIUM_PLAN],
  });
  return <PremiumPlanPurchaseModalForAfterPayment />;
};
export default function PremiumPlanPurchaseModalProvider({
  children,
}: ProviderProps) {
  const [premiumPlanPurchaseType, setPremiumPlanPurchaseType] = useState<
    PurchaseSupportedPremiumPlanType | undefined
  >();
  const [errorBannerProviderInitialValue, setErrorBannerProviderInitialValue] =
    useState<OpenModalOptions['errorBannerProviderInitialValue']>(undefined);
  const onCloseCallbackRef = useRef<OpenModalOptions['onClose']>(undefined);
  const onCloseCallback = onCloseCallbackRef.current;

  const [isModalChunkLoadPending, startTransition] = useTransition();

  const {
    creditProductPurchaseSuccessModal,
    openCreditProductPurchaseSuccessModal,
  } = useShowCreditProductPurchaseSuccessModal();

  const { getOpenRestrictionModalFunc } = useUnverifiedCompanyRestrictionModal({
    type: UnverifiedCompanyType.FEATURE_PURCHASE,
  });
  const openRestrictionModalFunc = getOpenRestrictionModalFunc();

  const openPremiumPlanPurchaseModal = ({
    callback,
    errorBannerProviderInitialValue,
    onClose,
    premiumPlanTypeToPurchase,
    trackingSource,
  }: OpenModalOptions) => {
    if (isModalChunkLoadPending) {
      return false;
    }

    if (openRestrictionModalFunc) {
      openRestrictionModalFunc();
      return false;
    }

    trackPremiumPlanPurchaseCTAClicked({
      planType: premiumPlanTypeToPurchase as SubscriptionPlanType,
      source: trackingSource,
    });

    setErrorBannerProviderInitialValue(errorBannerProviderInitialValue);
    onCloseCallbackRef.current = onClose;

    startTransition(() => {
      const planToPurchase = includes(
        PurchaseSupportedPremiumPlanType,
        premiumPlanTypeToPurchase
      )
        ? premiumPlanTypeToPurchase
        : premiumPlanTypeToPurchase === SubscriptionPlanType.FREE
          ? SubscriptionPlanType.GROWTH
          : SubscriptionPlanType.UNLIMITED;
      setPremiumPlanPurchaseType(
        planToPurchase as PurchaseSupportedPremiumPlanType
      );
      return callback?.();
    });
    return true;
  };

  const handleCloseModal = (
    creditProductPurchaseOrder?: CreditProductPurchaseOrderForAfterPayment
  ) => {
    onCloseCallback?.(creditProductPurchaseOrder);
    if (creditProductPurchaseOrder) {
      openCreditProductPurchaseSuccessModal(creditProductPurchaseOrder);
    }
    onCloseCallbackRef.current = undefined;
    setErrorBannerProviderInitialValue(undefined);
    setPremiumPlanPurchaseType(undefined);
  };

  return (
    <PremiumPlanPurchaseModalContext.Provider
      value={{
        openPremiumPlanPurchaseModal,
        isModalChunkLoadPending,
      }}
    >
      <ErrorBoundaryWithCrashReporting fallback={null}>
        <Suspense fallback={null}>
          <AfterPaymentStateHandlers />
          {creditProductPurchaseSuccessModal}
          {premiumPlanPurchaseType && (
            <PremiumPlanPurchaseModal
              errorBannerProviderInitialValue={errorBannerProviderInitialValue}
              premiumPlanTypeToPurchase={premiumPlanPurchaseType}
              onClose={handleCloseModal}
            />
          )}
        </Suspense>
      </ErrorBoundaryWithCrashReporting>
      {children}
    </PremiumPlanPurchaseModalContext.Provider>
  );
}
